
import React, { useState, useRef, useEffect } from 'react';
import { useGuitarMatch } from '../../context/GuitarMatchContext';
import { SeverityMatrix, LevelProfile } from '../../types';
import { generateLevelSweepSummary } from '../../services/testSuite';
import { getLastAnalysisRawPayload } from '../../services/analysisCache';
import { FACTORY_SEVERITY_MATRIX } from '../../services/severityEngine';

export const GodModePanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const { severityMatrix, updateSeverity, resetSeverityToFactory, result, renormalize } = useGuitarMatch();
    
    const [localMatrix, setLocalMatrix] = useState<SeverityMatrix>(() => {
        try {
            return JSON.parse(JSON.stringify(severityMatrix || FACTORY_SEVERITY_MATRIX));
        } catch (e) {
            return { ...FACTORY_SEVERITY_MATRIX };
        }
    });

    const [selectedLevel, setSelectedLevel] = useState(4);
    const [hasChanges, setHasChanges] = useState(false);
    const [sweepText, setSweepText] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [justCopied, setJustCopied] = useState(false);

    const terminalRef = useRef<HTMLDivElement>(null);

    const activeProfile = localMatrix[selectedLevel] || FACTORY_SEVERITY_MATRIX[selectedLevel];

    useEffect(() => {
        if (sweepText && terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
    }, [sweepText]);

    const handleGenerateSweep = () => {
        const sessionId = result?.audit_trail?.scoring_ruleset_id?.replace("GSC_SESSION_", "");
        const raw = getLastAnalysisRawPayload(sessionId);
        
        if (!result || !raw) {
            alert("Erreur : Aucune donnée de prise active. Réalisez un match avant de tester les réglages.");
            return;
        }

        setIsGenerating(true);
        // Utilisation d'une petite pause pour laisser l'UI respirer
        setTimeout(() => {
            try {
                // On passe la matrice LOCALE pour voir l'effet des sliders actuels
                const summary = generateLevelSweepSummary(result, localMatrix);
                setSweepText(summary);
                
                navigator.clipboard.writeText(summary).then(() => {
                    setJustCopied(true);
                    setTimeout(() => setJustCopied(false), 2000);
                });
            } catch (e) {
                console.error(e);
                alert("Échec de la simulation de sweep.");
            } finally {
                setIsGenerating(false);
            }
        }, 300);
    };

    const updateField = (category: keyof LevelProfile, field: string, value: any) => {
        const next = { ...localMatrix };
        if (category === 'weights') {
            (next[selectedLevel].weights as any)[field] = value;
        } else {
            const categoryData = { ...next[selectedLevel][category] as any };
            categoryData[field] = value;
            next[selectedLevel] = { ...next[selectedLevel], [category]: categoryData };
        }
        setLocalMatrix(next);
        setHasChanges(true);
    };

    const handleSave = () => {
        updateSeverity(localMatrix);
        setHasChanges(false);
        renormalize(selectedLevel); 
        alert("Matrice de Sévérité mise à jour.");
    };

    const TuningSlider = ({ label, category, field, min, max, step, unit = "", desc = "" }: any) => {
        const currentVal = category === 'weights' 
            ? (activeProfile.weights as any)[field] 
            : (activeProfile[category as keyof LevelProfile] as any)[field];

        return (
            <div className="space-y-2 bg-white/5 p-4 rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                        <span className="text-[7px] text-slate-600 uppercase italic">{desc}</span>
                    </div>
                    <span className="text-xs font-black text-smart-gold tabular-nums">
                        {currentVal}{unit}
                    </span>
                </div>
                <input 
                    type="range" min={min} max={max} step={step} 
                    value={currentVal}
                    onChange={(e) => updateField(category, field, parseFloat(e.target.value))}
                    className="w-full h-1 bg-white/10 rounded-full appearance-none accent-smart-accent cursor-pointer"
                />
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[60000] flex items-center justify-center p-4 md:p-10 font-sans">
            <div className="absolute inset-0 bg-black/95 backdrop-blur-3xl" onClick={onClose}></div>
            
            <div className="relative w-full max-w-7xl h-full max-h-[92vh] bg-[#020506] border border-smart-accent/30 rounded-[64px] shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">
                
                <header className="p-8 border-b border-white/10 bg-black/40 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-6">
                        <div className="w-14 h-14 rounded-2xl bg-smart-accent flex items-center justify-center text-3xl shadow-[0_0_30px_rgba(255,98,97,0.3)]">🎛️</div>
                        <div>
                            <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter leading-none">Neural Core <span className="text-smart-accent">Tuner</span></h2>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.4em] mt-1">Calibration fine des modules de jugement</p>
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <button 
                            onClick={handleGenerateSweep} 
                            disabled={isGenerating}
                            className={`px-8 py-3 rounded-full text-[10px] font-black uppercase shadow-xl transition-all flex items-center gap-3 ${isGenerating ? 'bg-slate-700 text-slate-500' : justCopied ? 'bg-emerald-500 text-black' : 'bg-cyan-500 text-black hover:scale-105 active:scale-95'}`}
                        >
                            {isGenerating ? 'Simulation...' : justCopied ? 'COPIÉ ! ✓' : 'Générer Sweep Report 📊'}
                        </button>
                        <button onClick={resetSeverityToFactory} className="px-6 py-3 bg-white/5 border border-white/10 text-white rounded-full text-[10px] font-black uppercase hover:bg-red-500/20 transition-all">Reset Factory</button>
                        <button onClick={onClose} className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors">✕</button>
                    </div>
                </header>

                <div className="flex-1 flex overflow-hidden">
                    <aside className="w-24 border-r border-white/5 flex flex-col gap-2 p-4 bg-black/20 overflow-y-auto no-scrollbar">
                        {Array.from({length: 8}).map((_, i) => (
                            <button 
                                key={i+1} 
                                onClick={() => setSelectedLevel(i+1)} 
                                className={`h-20 shrink-0 rounded-2xl flex flex-col items-center justify-center transition-all ${selectedLevel === i+1 ? 'bg-smart-accent text-white scale-110 shadow-lg' : 'bg-white/5 text-slate-500 hover:bg-white/10'}`}
                            >
                                <span className="text-2xl font-black italic">L{i+1}</span>
                                <span className="text-[7px] font-bold uppercase opacity-60">Config</span>
                            </button>
                        ))}
                    </aside>

                    <main className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-y-auto p-10 custom-scrollbar">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <section className="space-y-6">
                                    <h4 className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.4em] border-l-2 border-cyan-400 pl-4">Poids du Score (%)</h4>
                                    <TuningSlider label="Poids Rythme" category="weights" field="timing" min={0} max={100} step={5} unit="%" />
                                    <TuningSlider label="Poids Stabilité" category="weights" field="stability" min={0} max={100} step={5} unit="%" />
                                    <TuningSlider label="Poids Harmonie" category="weights" field="harmony" min={0} max={100} step={5} unit="%" />
                                    <TuningSlider label="Poids Attaque" category="weights" field="articulation" min={0} max={100} step={5} unit="%" />
                                    <TuningSlider label="Poids Nuance" category="weights" field="nuance" min={0} max={100} step={5} unit="%" />
                                    <TuningSlider label="Poids Posture" category="weights" field="posture" min={0} max={100} step={5} unit="%" />
                                    <TuningSlider label="Poids Propreté" category="weights" field="cleanliness" min={0} max={100} step={5} unit="%" />
                                </section>

                                <section className="space-y-6">
                                    <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.4em] border-l-2 border-emerald-400 pl-4">Paramètres DSP</h4>
                                    <TuningSlider label="Tolérance Rythme" category="timing" field="toleranceMsBase" min={10} max={1000} step={5} unit="ms" />
                                    <TuningSlider label="Stabilité Balancier" category="stability" field="maxJitterMs" min={10} max={400} step={5} unit="ms" />
                                    <TuningSlider label="Seuil Harmonie" category="harmony" field="minChordConfidence" min={10} max={98} step={1} unit="%" />
                                </section>

                                <section className="space-y-6">
                                    <h4 className="text-[10px] font-black text-red-400 uppercase tracking-[0.4em] border-l-2 border-red-400 pl-4">Muting & Propreté</h4>
                                    <TuningSlider label="Bruits Max" category="cleanliness" field="maxGhostsPerMin" min={0} max={50} step={1} unit="/min" />
                                    <TuningSlider label="Pénalité Bruit" category="cleanliness" field="ghostPenaltyWeightPerGhost" min={0.1} max={10} step={0.1} desc="Impact pts par erreur" />
                                </section>
                            </div>
                        </div>

                        {/* TERMINAL DE SWEEP */}
                        {sweepText && (
                            <div className="h-48 border-t border-white/10 bg-black p-6 font-mono text-[10px] text-cyan-500 overflow-hidden flex flex-col gap-2">
                                <div className="flex justify-between items-center opacity-50">
                                    <span className="uppercase tracking-widest">Neural Simulator v1.0 - Output Terminal</span>
                                    <button onClick={() => setSweepText("")} className="hover:text-white">Close X</button>
                                </div>
                                <div ref={terminalRef} className="flex-1 overflow-y-auto whitespace-pre custom-scrollbar">
                                    {sweepText}
                                </div>
                            </div>
                        )}
                    </main>
                </div>

                <footer className="p-8 border-t border-white/10 bg-black/40 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-4 text-slate-500">
                        <span className="text-[8px] font-black uppercase tracking-widest">Édition L{selectedLevel} : {activeProfile.label}</span>
                    </div>
                    <div className="flex gap-4">
                        <button onClick={onClose} className="px-10 py-4 bg-white/5 text-slate-400 rounded-full font-black uppercase text-xs tracking-widest hover:text-white transition-all">Annuler</button>
                        <button 
                            onClick={handleSave} 
                            disabled={!hasChanges} 
                            className={`px-16 py-4 rounded-full font-black uppercase text-sm tracking-[0.4em] shadow-2xl transition-all ${hasChanges ? 'bg-smart-accent text-white scale-105' : 'bg-slate-800 text-slate-600'}`}
                        >
                            SAUVEGARDER ⚡
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};
